import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getColumnsIDs, getHiddenColumns } from 'modules/Statistics/utils'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useColumnConfig from 'modules/Statistics/Main/subcomponents/TableComponent/columnsConfig'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Table from 'modules/Statistics/Layout/Table'
import PropTypes from 'prop-types'
import { notificationShow } from 'modules/Notifications/actions'
import { URL_FILE_XLS } from 'api/statistics/v2'
import queryString from 'query-string'
import prepareFilterParams from 'utils/prepareFilterParams'

const TableComponent = ({ filters, isDemoMode }) => {
  const newWindow = useRef(window)
  const { table } = useSelector(({ statistics }) => statistics)
  const { t } = useTranslation(['statistics'])
  const [tableParams, setTableParams] = useState({})
  const dispatch = useDispatch()
  const [sorting, setSorting] = useState([])

  const messageListener = useCallback(
    (e) => {
      let key = e.message ? 'message' : 'data'
      let data = e[key]

      if (e.origin !== window.location.origin) {
        return
      }

      switch (data) {
        case 'accountAddSuccess':
          newWindow.current && newWindow.current.close()
          newWindow.current = null
          dispatch(
            notificationShow({
              type: 'success',
              message: 'common::notifications.success_connected',
            }),
          )

          dispatch({
            type: 'STATISTICS/DROP_TABLE_DATA',
          })

          dispatch({
            type: 'STATISTICS/FETCH_TABLE',
            payload: tableParams,
            isDemoMode,
          })
          break

        case 'accountRejectConnection':
          newWindow.current && newWindow.current.close()
          dispatch(
            notificationShow({
              type: 'error',
              message: 'statistics::forms.account_connect.rejected',
            }),
          )
          break

        case 'accountGoBackConnection':
          newWindow.current && newWindow.current.close()
          break
      }
    },
    [tableParams, dispatch, newWindow],
  )

  useEffect(() => {
    let addEventMethod = window.addEventListener
      ? 'addEventListener'
      : 'attachEvent'

    let eventer = window[addEventMethod]

    let messageEvent =
      addEventMethod === 'attachEvent' ? 'onmessage' : 'message'

    eventer(messageEvent, messageListener, false)

    return () => {
      let removeEventMethod = window.removeEventListener
        ? 'removeEventListener'
        : 'detachEvent'

      let eventer = window[removeEventMethod]

      let messageEvent =
        removeEventMethod === 'detachEvent' ? 'onmessage' : 'message'

      eventer(messageEvent, messageListener)
    }
  }, [messageListener])

  const [statisticsMainTableConfig, setStatisticsMainTableConfig] =
    useLocalStorage('statisticsMainTableConfig', { hiddenColumns: {} })

  const [tablePagination, setTablePagination] = useLocalStorage(
    'statisticsTablePagination',
    { limit: 25 },
  )

  const [tableGroup, setTableGroup] = useLocalStorage('statisticsTableGroup', {
    tableGroupBy: 'accounts',
  })

  const [limit, setLimit] = useState(tablePagination.limit)
  const [groupBy, setGroupBy] = useState(tableGroup.tableGroupBy)

  const callbacks = {
    createConnectWindow: (e) => {
      e.preventDefault()
      newWindow.current = window.open(e.target.getAttribute('href'))

      newWindow.current.focus()
      let timer = setInterval(() => {
        if (newWindow.current) {
          if (newWindow.current.closed) {
            window.parent.postMessage(
              'accountRejectConnection',
              location.origin,
            )
            clearInterval(timer)
          }
        } else {
          clearInterval(timer)
        }
      }, 500)
    },
  }

  const columns = useColumnConfig(
    filters.include_vat,
    table.data,
    table.summary,
    groupBy,
    callbacks,
  )

  const columnsShown = useMemo(() => {
    const { hiddenColumns } = statisticsMainTableConfig
    return columns.filter(
      ({ id, hideable = true }) => !hideable || !(id in hiddenColumns),
    )
  }, [statisticsMainTableConfig, columns])

  useEffect(() => setTableGroup({ tableGroupBy: groupBy }), [groupBy])
  useEffect(() => setTablePagination({ limit: limit }), [limit])

  const fileUrl = useCallback(
    (url) => {
      const { id = null, desc = false } = sorting?.[0] ?? {}

      const sortingProp = id
        ? { sort: id, direction: desc ? 'desc' : 'asc' }
        : {}

      const search = queryString.stringify(
        prepareFilterParams({
          ...filters,
          columns: columnsShown
            .filter(({ id }) => id !== 'account.id')
            .map(({ id }) =>
              id === 'cost' && filters?.include_vat ? 'cost_with_vat' : id,
            ),
          offset: 0,
          ...sortingProp,
        }),
        {
          arrayFormat: 'bracket',
        },
      )

      return url + '?' + search
    },
    [filters, sorting, columnsShown],
  )

  return (
    <Table
      isDemoMode={isDemoMode}
      {...table}
      groupBy={groupBy}
      limit={limit}
      filters={filters}
      columns={columnsShown}
      exportButtons={[
        {
          type: 'xls',
          fileUrl: fileUrl(URL_FILE_XLS({ groupBy })),
        },
      ]}
      tableSwitchers={[
        {
          name: 'table-group-switcher',
          value: {
            value: groupBy,
            label: `${t('statistics::table_switcher.group_by')}: ${t(
              'statistics::table_switcher.by',
              { context: groupBy },
            )}`,
          },
          values: [
            {
              value: 'accounts',
              label: t('statistics::table_switcher.by', {
                context: 'accounts',
              }),
            },
            {
              value: 'networks',
              label: t('statistics::table_switcher.by', {
                context: 'networks',
              }),
            },
          ],
          onChange: setGroupBy,
        },
        {
          name: 'pagination',
          value: {
            value: limit,
            label: `${t('statistics::pagination.limit_button_label')} ${limit}`,
          },
          values: [
            {
              value: 25,
              label: t('statistics::pagination.limit_by', { pages: 25 }),
            },
            {
              value: 50,
              label: t('statistics::pagination.limit_by', { pages: 50 }),
            },
            {
              value: 100,
              label: t('statistics::pagination.limit_by', { pages: 100 }),
            },
          ],
          onChange: setLimit,
        },
        {
          name: 'columns',
          type: 'multiple',
          placeholder: t(
            'statistics::table_switcher.choose_columns_placeholder',
          ),
          listTitle: t('statistics::table_switcher.choose_columns'),
          value: getColumnsIDs(columnsShown),
          values: columns
            .filter(({ hideable = true }) => hideable)
            .map(({ id, Header = '' }) => ({
              value: id,
              label: Header,
            })),
          onChange: (selectionArr) =>
            setStatisticsMainTableConfig(
              getHiddenColumns(selectionArr, columns),
            ),
          onReset: () =>
            setStatisticsMainTableConfig(
              getHiddenColumns(getColumnsIDs(columns), columns),
            ),
        },
      ]}
      initialState={{
        sortBy: [
          {
            id: groupBy === 'accounts' ? 'account.network' : 'network',
            asc: true,
          },
        ],
      }}
      dropTableData={() =>
        dispatch({
          type: 'STATISTICS/DROP_TABLE_DATA',
        })
      }
      onTableParamsUpdate={(params) => {
        dispatch({
          type: 'STATISTICS/FETCH_TABLE',
          payload: params,
          isDemoMode,
        })
        setTableParams(params)
      }}
      onSortingChange={setSorting}
    />
  )
}

TableComponent.propTypes = {
  filters: PropTypes.object.isRequired,
  isDemoMode: PropTypes.bool,
}

export default TableComponent
